// src/components/ConditionStatementButton.jsx

import { useState } from 'react';
import PropTypes from 'prop-types';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { Tooltip } from 'react-tooltip';
import { CgSpinner } from 'react-icons/cg';
import { getAccountConditionsStatement } from '../../api/api';
import { useTranslation } from 'react-i18next';
import { downloadPdfFromBase64 } from '../../lib/utils';

function ConditionStatementButton({ accountID }) {
  const [isLoading, setIsLoading] = useState(false);
  const [showNoContentModal, setShowNoContentModal] = useState(false);
  const { t } = useTranslation();

  const handleDownload = async () => {
    if (isLoading) return;
  
    setIsLoading(true);
    try {
      const response = await getAccountConditionsStatement(accountID);
      if (response?.file && response?.name && response?.extension) {
        const success = downloadPdfFromBase64(
          response.file,
          `${response.name}.${response.extension}`
        );
        
        if (!success) {
          setShowNoContentModal(true);
        }
      } else {
        setShowNoContentModal(true);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setShowNoContentModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setShowNoContentModal(false);
  };

  return (
    <>
      <button
        onClick={handleDownload}
        className={`rounded-full bg-blue-500 hover:bg-blue-700 p-2 text-white ${isLoading ? 'cursor-not-allowed opacity-50' : ''}`}
        data-tooltip-id="conditionStatement"
        data-tooltip-content={t('conditionStatement.tooltipContent')}
        disabled={isLoading}
      >
        {isLoading ? (
          <CgSpinner className="animate-spin" />
        ) : (
          <HiOutlineDocumentText />
        )}
      </button>
      <Tooltip id="conditionStatement" place="bottom" effect="solid" />

      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg flex flex-col items-center">
            <CgSpinner className="animate-spin text-4xl text-blue-500 mb-4" />
            <p className="text-lg font-semibold">{t('conditionStatement.generatingDocument')}</p>
            <p className="text-sm text-gray-600 mt-2">{t('conditionStatement.pleaseWait')}</p>
          </div>
        </div>
      )}

      {showNoContentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-4">{t('conditionStatement.noContentTitle')}</h2>
            <p>{t('conditionStatement.noContentMessage')}</p>
            <button
              onClick={closeModal}
              className="mt-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              {t('conditionStatement.close')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

ConditionStatementButton.propTypes = {
  accountID: PropTypes.string.isRequired,
};

export default ConditionStatementButton;